import { useRef, useState, type MutableRefObject, type PointerEvent } from "react";
import { Scene } from "@babylonjs/core/scene";
import { ArcRotateCamera } from "@babylonjs/core/Cameras/arcRotateCamera";

const BASE_SIZE = 180;
const KNOB_SIZE = 96;
const MAX_OFFSET = (BASE_SIZE - KNOB_SIZE) / 2;
const SENSITIVITY_ALPHA = 0.05;
const SENSITIVITY_BETA = 0.03;

type Props = {
  sceneRef: MutableRefObject<Scene | null>;
};

type DragState = {
  pointerId: number;
  rafId: number | null;
  vec: { x: number; y: number };
};

export const SceneJoystick = ({ sceneRef }: Props) => {
  const baseRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);
  const [knob, setKnob] = useState({ x: 0, y: 0 });

  const tick = () => {
    const drag = dragRef.current;
    if (!drag) return;
    const camera = sceneRef.current?.getCameraByName("camera") as ArcRotateCamera | null;
    if (camera) {
      camera.alpha -= drag.vec.x * SENSITIVITY_ALPHA;
      camera.beta -= drag.vec.y * SENSITIVITY_BETA;
      if (camera.lowerBetaLimit !== undefined && camera.beta < camera.lowerBetaLimit) {
        camera.beta = camera.lowerBetaLimit;
      }
      if (camera.upperBetaLimit !== undefined && camera.beta > camera.upperBetaLimit) {
        camera.beta = camera.upperBetaLimit;
      }
    }
    drag.rafId = requestAnimationFrame(tick);
  };

  const handlePointerDown = (e: PointerEvent<HTMLDivElement>) => {
    e.currentTarget.setPointerCapture(e.pointerId);
    dragRef.current = { pointerId: e.pointerId, rafId: null, vec: { x: 0, y: 0 } };
    dragRef.current.rafId = requestAnimationFrame(tick);
  };

  const handlePointerMove = (e: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId || !baseRef.current) return;
    const rect = baseRef.current.getBoundingClientRect();
    const cx = rect.left + rect.width / 2;
    const cy = rect.top + rect.height / 2;
    let dx = e.clientX - cx;
    let dy = e.clientY - cy;
    const len = Math.hypot(dx, dy);
    if (len > MAX_OFFSET) {
      dx = (dx / len) * MAX_OFFSET;
      dy = (dy / len) * MAX_OFFSET;
    }
    setKnob({ x: dx, y: dy });
    drag.vec.x = dx / MAX_OFFSET;
    drag.vec.y = dy / MAX_OFFSET;
  };

  const handlePointerUp = (e: PointerEvent<HTMLDivElement>) => {
    const drag = dragRef.current;
    if (!drag || drag.pointerId !== e.pointerId) return;
    if (e.currentTarget.hasPointerCapture(e.pointerId)) {
      e.currentTarget.releasePointerCapture(e.pointerId);
    }
    if (drag.rafId !== null) cancelAnimationFrame(drag.rafId);
    dragRef.current = null;
    setKnob({ x: 0, y: 0 });
  };

  return (
    <div
      ref={baseRef}
      className="fixed bottom-4 left-4 w-[180px] h-[180px] rounded-full bg-white/70 z-50 pointer-events-auto select-none touch-none"
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={handlePointerUp}
    >
      <div
        className="absolute left-1/2 top-1/2 w-[96px] h-[96px] rounded-full bg-white shadow-lg"
        style={{
          transform: `translate(calc(-50% + ${knob.x}px), calc(-50% + ${knob.y}px))`,
        }}
      />
    </div>
  );
};
